import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = ["search", "candidates", "selected", "save", "pickerHint", "position", "positionLabel", "positionCopy", "endDate"]
  static values = { country: String, category: String, slot: String, searchUrl: String }

  connect() {
    this.searchTimer = null
    if (this.element.classList.contains("admin-premium-drawer")) document.body.style.overflow = "hidden"
  }

  disconnect() {
    window.clearTimeout(this.searchTimer)
    if (this.element.classList.contains("admin-premium-drawer")) document.body.style.overflow = ""
  }

  async open(event) {
    if (event.type === "keydown" && event.key !== "Enter") return
    event.preventDefault()

    const url = event.currentTarget.dataset.url
    const root = document.getElementById("admin-drawer-root")
    if (!url || !root) return

    try {
      const response = await fetch(url, { headers: { Accept: "text/html" }, credentials: "same-origin" })
      if (!response.ok) throw new Error("The placement editor could not be loaded.")
      root.innerHTML = await response.text()
    } catch (error) {
      Swal.fire({ icon: "error", title: "Couldn’t open placement", text: error.message })
    }
  }

  close(event) {
    event?.preventDefault()
    const root = document.getElementById("admin-drawer-root")
    if (root) root.innerHTML = ""
    document.body.style.overflow = ""
  }

  queueSearch(event) {
    if (event.isComposing) return
    window.clearTimeout(this.searchTimer)
    this.searchTimer = window.setTimeout(() => this.search(), 425)
  }

  async search() {
    if (!this.hasSearchUrlValue || !this.hasCandidatesTarget) return

    const url = new URL(this.searchUrlValue, window.location.origin)
    url.searchParams.set("country", this.countryValue)
    url.searchParams.set("category", this.categoryValue)
    const query = this.searchTarget.value.trim()
    if (query) url.searchParams.set("query", query)
    if (this.selectedTarget.value) url.searchParams.set("selected_charity_id", this.selectedTarget.value)

    this.candidatesTarget.classList.add("is-searching")
    try {
      const response = await fetch(url, { headers: { Accept: "text/html" }, credentials: "same-origin" })
      if (!response.ok) throw new Error("Charities could not be loaded.")
      this.candidatesTarget.innerHTML = await response.text()
      if (this.hasPickerHintTarget) {
        this.pickerHintTarget.textContent = query
          ? "Searching every active charity in this country."
          : "Showing active charities that already have a premium placement. Search to find any active charity in this country."
      }
    } catch (error) {
      Swal.fire({ icon: "error", title: "Search unavailable", text: error.message })
    } finally {
      this.candidatesTarget.classList.remove("is-searching")
    }
  }

  selectCharity(event) {
    event.preventDefault()
    const button = event.currentTarget
    this.selectedTarget.value = button.dataset.charityId
    this.candidatesTarget.querySelectorAll(".admin-premium-candidate").forEach((candidate) => {
      candidate.classList.toggle("selected", candidate === button)
    })
    if (this.hasSaveTarget) this.saveTarget.disabled = false
  }

  positionDown() {
    this.setPosition(this.currentPosition() - 1)
  }

  positionUp() {
    this.setPosition(this.currentPosition() + 1)
  }

  setPosition(value) {
    if (!this.hasPositionTarget) return
    const min = Number(this.positionTarget.min || 1)
    const max = Number(this.positionTarget.max || value)
    const next = Math.max(min, Math.min(max, value))
    this.positionTarget.value = next
    if (this.hasPositionLabelTarget) this.positionLabelTarget.textContent = next
    if (this.hasPositionCopyTarget) this.positionCopyTarget.textContent = next
  }

  currentPosition() {
    return Number(this.positionTarget.value || 1)
  }

  setEndDate(event) {
    event.preventDefault()
    const date = new Date()
    date.setHours(12, 0, 0, 0)

    if (event.currentTarget.dataset.endOfYear === "true") {
      date.setMonth(11, 31)
    } else {
      date.setDate(date.getDate() + Number(event.currentTarget.dataset.days || 0))
    }

    const value = [date.getFullYear(), String(date.getMonth() + 1).padStart(2, "0"), String(date.getDate()).padStart(2, "0")].join("-")
    if (this.endDateTarget._flatpickr) this.endDateTarget._flatpickr.setDate(value, true)
    else this.endDateTarget.value = value
  }

  async clear(event) {
    event.preventDefault()
    event.stopPropagation()
    const button = event.currentTarget
    const result = await Swal.fire({
      icon: "warning",
      title: "Clear this placement?",
      text: `${button.dataset.charityName || "This charity"} will no longer occupy this position. A shared fallback may appear in its place.`,
      showCancelButton: true,
      confirmButtonText: "Clear spot",
      cancelButtonText: "Keep it",
      confirmButtonColor: "#d65b7e"
    })
    if (!result.isConfirmed) return

    await this.request(button.dataset.url, "DELETE")
  }

  async request(url, method) {
    if (!url) return
    const token = document.querySelector("meta[name='csrf-token']")?.content

    try {
      const response = await fetch(url, {
        method,
        credentials: "same-origin",
        headers: { Accept: "text/vnd.turbo-stream.html", "X-CSRF-Token": token }
      })
      const body = await response.text()
      if (body) Turbo.renderStreamMessage(body)
      if (!response.ok) throw new Error("The placement could not be changed.")
    } catch (error) {
      Swal.fire({ icon: "error", title: "Change not saved", text: error.message })
    }
  }
}
